import React from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import ListViewSVG from '@/images/home/ListView.svg?react'
import MapViewSVG from '@/images/home/MapView.svg?react'
import DumbbellSVG from '@/images/home/categories/Dumbbell.svg?react'
import RunningFigureSVG from '@/images/home/categories/RunningFigure.svg?react'
import GraduationCapSVG from '@/images/home/categories/GraduationCap.svg?react'
import TranslationNS from '@/utils/Constants/TranslationNS'
import { useMin } from '@/hooks/useBreakpoint'
import useScrolled from '@/hooks/useScrolled'
import { Constants } from '@/utils'
import { parseSearchParams } from '@/utils/homeSearch'
import SButton from '@/components/common/SButton'
import CreateEventButton from '@/components/Navbar/CreateEventButton'

import './index.css'

const CATEGORY_ICON = {
    'Functional Fitness': DumbbellSVG,
    'Hybrid Race': RunningFigureSVG,
    Workshop: GraduationCapSVG,
}

const { CATEGORIES } = Constants.EVENT

/**
 * Category chips + List/Map toggle (kit .hbar). Sits 20px under the nav (kit shell gap
 * 16 + .hbar margin 4) and 16px above the grid, aligned on the content column. Once the
 * page has scrolled (kit body.nav-stuck .hbar) it sticks at top 78 as a white card:
 * radius 20, 12/16 padding, --nav-bd border, soft lift, spanning the 1220px content
 * column (full width minus the 20px page insets on mobile). Chips wrap onto several
 * rows at narrow widths (kit .hcats flex-wrap); nothing scrolls or truncates.
 * After the chips, behind a hairline, "Create an Event" (William 15 Oct 2026: always visible,
 * logged in or not — the context routes visitors through the login). Under 720px (where the
 * List/Map control already takes the whole row) the four buttons form a 2-column grid of equal
 * cells instead of wrapping, and the hairline goes.
 */
const SearchBar = ({ showMap, onClickShowMap }) => {
    const { t } = useTranslation(TranslationNS.HOME_PAGE)
    const [searchParams, setSearchParams] = useSearchParams()

    const search = parseSearchParams(searchParams)

    // Switch desktop/mobile layouts at the `nav` breakpoint.
    const isDesktop = useMin('nav')

    const isScrolled = useScrolled()

    const categoriesOptions = CATEGORIES.map((o) => ({
        value: o,
        label: t(o),
        disabled: !search.categories.includes(o),
    }))

    // Single-select (mock `state.cat`): a chip toggles itself off, another chip replaces it.
    const handleCategoryOnClick = (value) => {
        const next = search.categories.includes(value) ? [] : [value]

        setSearchParams((previous) => {
            previous.delete('categories')
            next.forEach((c) => previous.append('categories', c))
            return previous
        })
    }

    // Stuck card width: the 1220px content column (nav width minus 2 x 35) on desktop,
    // the 20px page insets on mobile.
    const stuckWidth = isDesktop
        ? 'max-w-[min(calc(100%-var(--x003)-2*var(--x001)),calc(var(--x005)-2*var(--x001)))]!'
        : 'max-w-[calc(100%-40px)]!'

    return (
        <div
            className={`
                ${showMap ? 'relative' : 'sticky top-(--navbar-height)'} left-0 right-0 z-99
                flex flex-wrap items-center justify-between gap-3
                w-full mx-auto box-border
                rounded-[20px]
                border border-transparent
                bg-transparent
                transition-[border-color,box-shadow,top,max-width,background,padding]
                duration-250 ease-in-out motion-reduce:transition-none

                ${
                    isDesktop
                        ? 'max-w-[min(calc(100%-var(--x003)),var(--x005))] px-(--x001) pt-5 pb-4'
                        : 'max-w-full px-5 pt-5 pb-4'
                }

                ${
                    isScrolled
                        ? `
                            top-[78px]!
                            py-3! px-4!
                            border-(--nav-bd)!
                            bg-white
                            shadow-[0_10px_26px_rgba(14,16,23,.10)]
                            ${stuckWidth}
                        `
                        : ''
                }
            `}
        >
            {/* kit .chip: 32px, radius 5, ink border, Comp 14, 5px icon-to-label, 8px between
                chips; selected = ink fill. Wraps instead of scrolling (kit .hcats). */}
            <div className="flex min-w-0 flex-wrap gap-2 max-[719px]:grid max-[719px]:grid-cols-2">
                {/* guided tour anchors (home tour, 29 Oct 2026): the chips as one block on desktop; under
                    720 the block dissolves into the grid (`contents`, unpainted) and the first chip, which
                    carries the same anchor, takes over — findTarget keeps the first painted match */}
                <div className="flex flex-wrap gap-2 max-[719px]:contents" data-tour="home-categories">
                    {categoriesOptions.map((o, index) => (
                        <SButton
                            key={o.value}
                            data-tour={index === 0 ? 'home-categories' : undefined}
                            value={<span>{o.label}</span>}
                            onClick={() => handleCategoryOnClick(o.value)}
                            aria-pressed={!o.disabled}
                            className={`font-meta h-8! py-0! px-2.5! gap-[5px]! rounded-[5px]! font-normal! border shrink-0 max-[719px]:w-full ${
                                o.disabled ? 'bg-transparent! text-[#272727]!' : ''
                            }`}
                            Icon={CATEGORY_ICON[o.value]}
                            iconLocation="left"
                        />
                    ))}
                </div>

                {/* hairline + button stay together (same hairline as the desktop nav); on the mobile grid
                    the wrapper dissolves so the button is a cell of its own */}
                <div className="flex items-center gap-2 max-[719px]:contents">
                    <span aria-hidden className="h-[26px] w-px shrink-0 bg-(--nav-bd) max-[719px]:hidden" />
                    <CreateEventButton className="max-[719px]:w-full" data-tour="home-create" />
                </div>
            </div>

            <div className="map-toggle" role="group" aria-label={t('View mode')} data-tour="home-view">
                <button
                    type="button"
                    className={`map-toggle-option${!showMap ? ' map-toggle-option-active' : ''}`}
                    onClick={onClickShowMap}
                >
                    <ListViewSVG className="map-toggle-icon" />
                    {t('List')}
                </button>
                <button
                    type="button"
                    className={`map-toggle-option${showMap ? ' map-toggle-option-active' : ''}`}
                    onClick={onClickShowMap}
                >
                    <MapViewSVG className="map-toggle-icon" />
                    {t('Map')}
                </button>
            </div>
        </div>
    )
}

export default SearchBar
